import uuid from 'uuid';
import {qualify} from '@packages/systems/wfdl-Qualified';
import {
  Plugins,
  Component,
  Expressions,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {
  EElement,
  ERecord,
  EList,
  EText,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import type {WebflowCssProperties} from '@packages/systems/core/utils/WebflowCssSubset';
import type {BreakpointID} from '@packages/systems/style/types';
import type {PseudoStateKey} from '@packages/systems/core/utils/StyleTypes';
import {
  getChildren,
  getPageComponentFromComponentMap,
} from '@packages/utilities/platform';
import {forTestSuite as wfdlTestSuite} from '@packages/systems/wfdl-tools';
const {createComponentMapForTest} = wfdlTestSuite;

type MakeComponentMapWithLayout = {
  cellsHaveChildren?: boolean;
  cellsHaveStyleBlockIds?: boolean;
  layoutStyles?: {
    gridTemplateColumns: string;
    gridTemplateRows: string;
  };
  cellsNumber?: number;
};

export const makeComponentMapWithLayout = (
  options?: MakeComponentMapWithLayout
) => {
  const layoutId = uuid();
  const cellIds: Array<string> = [];

  // Default to a 2x2 Layout if no options are provided
  const numberOfCells = options?.cellsNumber || 4;
  for (let i = 0; i < numberOfCells; i++) {
    cellIds.push(uuid());
  }

  const componentMap = createComponentMapForTest(
    EElement({
      id: 'page-body',
      type: qualify(Plugins.name('Body'), Component.name('Body')),
      data: ERecord({
        children: EList([makeLayoutElement(layoutId, cellIds, options)]),
      }),
    })
  );

  return {
    componentMap,
    layoutId,
    cellIds,
  };
};

export const makeLayoutElement = (
  layoutId: string,
  cellIds: string[],
  options?: MakeComponentMapWithLayout
) =>
  EElement({
    id: layoutId,
    type: qualify(Plugins.name('Layout'), Component.name('Layout')),
    data: ERecord({
      children: EList(
        cellIds.map((cellId) => makeCellElement(cellId, null, options))
      ),
      style: ERecord({
        base: ERecord({
          main: ERecord({
            noPseudo: ERecord({
              display: EText('grid'),
              gridTemplateColumns: EText(
                options?.layoutStyles?.gridTemplateColumns || '1fr 1fr'
              ),
              gridTemplateRows: EText(
                options?.layoutStyles?.gridTemplateRows || 'auto auto'
              ),
            }),
          }),
        }),
      }),
    }),
  });

export const makeCellElement = (
  cellId: string,
  styles?: WebflowCssProperties | null,
  options?: MakeComponentMapWithLayout
) => {
  return EElement({
    id: cellId,
    type: qualify(Plugins.name('Layout'), Component.name('Cell')),
    data: ERecord({
      children: options?.cellsHaveChildren ? cellChildren() : EList([]),
      ...(styles && {
        style: ERecord({
          base: ERecord({
            main: ERecord({
              noPseudo: ERecord(
                Object.fromEntries(
                  Object.entries(styles)
                    .filter(([, val]) => typeof val === 'string')
                    .map(([key, val]) => [key, EText(String(val))])
                )
              ),
            }),
          }),
        }),
      }),
      ...(options?.cellsHaveStyleBlockIds && {
        styleBlockIds: EList([EText(`style-block-id-${cellId}`)]),
      }),
    }),
  });
};

const cellChildren = () =>
  EList([
    EElement({
      id: 'cell-child-1',
      type: qualify(Plugins.name('Basic'), Component.name('Paragraph')),
      data: ERecord({}),
    }),
    EElement({
      id: 'cell-child-2',
      type: qualify(Plugins.name('Basic'), Component.name('Heading')),
      data: ERecord({}),
    }),
  ]);

export const getLayoutElement = (
  layoutId: string,
  componentMap: Component.ComponentMap
): ExpressionElement => {
  const pageComponent = getPageComponentFromComponentMap(componentMap);

  if (!pageComponent) {
    throw new Error(
      'Page component not found in tests componentMap data. Make sure the test data is formed correctly.'
    );
  }

  const layoutElement = Expressions.getElementById(
    Component.getRender(pageComponent),
    layoutId
  );

  if (!layoutElement) {
    throw new Error(
      'Layout not found in tests componentMap data. Make sure the test data is formed correctly.'
    );
  }

  return layoutElement;
};

export const getLayoutCells = (
  layoutId: string,
  componentMap: Component.ComponentMap
): ExpressionElement[] => {
  const layoutElement = getLayoutElement(layoutId, componentMap);
  return getChildren(layoutElement) || [];
};

const generateStylePath = (
  breakpointId: BreakpointID,
  pseudoKey: PseudoStateKey
): ExpressionPath => {
  return [
    {in: 'Record', at: 'style'},
    {in: 'Record', at: 'base'},
    {in: 'Record', at: breakpointId},
    {in: 'Record', at: pseudoKey},
  ];
};

export const getElementLocalStyles = (
  element: ExpressionElement,
  breakpointId: BreakpointID,
  pseudoMode: PseudoStateKey
): {
  [key: string]: string;
} => {
  const idStylesData = Expressions.getIn(
    Expressions.getElementData(element),
    generateStylePath(breakpointId, pseudoMode)
  );
  if (!idStylesData) {
    return {};
  }

  const idStylesRecord = Expressions.getRecord(idStylesData);
  if (!idStylesRecord) {
    return {};
  }

  const localStyles: Record<string, any> = {};
  Object.entries(idStylesRecord).forEach(([key, expr]: [any, any]) => {
    const value = Expressions.getText(expr);
    if (value) {
      localStyles[key] = value;
    }
  });

  return localStyles;
};
